"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.adminWinnerContract = void 0;
const types_1 = require("@daymade/common-lib/types");
const core_1 = require("@ts-rest/core");
const zod_1 = require("zod");
const models_1 = require("../prize/models");
const utils_1 = require("../utils");
const models_2 = require("./models");
const ZGetPrizeWinsResponse = models_2.ZPrizeWinSelect.extend({
    drawId: zod_1.z.number(),
    drawName: zod_1.z.string(),
    drawTypeName: zod_1.z.string().nullable(),
    drawTypeGroup: zod_1.z.string().nullable(),
    gameType: zod_1.z.string(),
    entry: models_2.ZTicketEntrySelect.shape.entry,
    drawResult: models_2.ZDrawSelect.shape.result.nullable(),
    prizeStructureCode: zod_1.z.string(),
    prizeTierCode: zod_1.z.string(),
    prizeTierType: zod_1.z.string(),
    prizeTierValue: zod_1.z.number().nullable(),
    playerId: zod_1.z.string(),
    playerName: zod_1.z.string(),
    playerEmailAddress: zod_1.z.string(),
    playerPhoneNumber: zod_1.z.string().nullable(),
    chosenPrizeName: zod_1.z.string().nullable(),
    chosenPrizeDescription: zod_1.z.string().nullable(),
    assigneeName: zod_1.z.string().nullable(),
    prizeOrder: models_2.ZPrizeOrderSelect.nullable(),
    products: models_2.ZPrizeOrderProductSelect.merge(models_1.ZProductSelect.pick({
        name: true,
        price: true,
        productCode: true,
        discountCode: true,
        purchaseFrom: true,
    })).array(),
}).array();
const ZUpdatePrizeWinRequest = models_2.ZPrizeWinInsert.pick({
    id: true,
    notes: true,
    expiresAt: true,
}).required({
    id: true,
});
const ZUpdatePrizeWinResponse = models_2.ZPrizeWinSelect;
const ZGetPrizeOrdersResponse = models_2.ZPrizeOrderSelect.extend({
    prizeName: zod_1.z.string(),
    prizeInternalInfo: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
    brand: zod_1.z.string().nullable(),
    drawId: zod_1.z.number().nullable(),
    gameType: models_2.ZDrawTypeSelect.shape.gameType.nullable(),
    drawTypeGroup: zod_1.z.string().nullable(),
    prizeStructureCode: zod_1.z.string().nullable(),
    prizeTierCode: zod_1.z.string().nullable(),
    tokenRedemptionId: zod_1.z.string().nullable(),
    tokenRedemptionOptionTitle: zod_1.z.string().nullable(),
    playerName: zod_1.z.string(),
    playerEmail: zod_1.z.string(),
    assigneeName: zod_1.z.string().nullable(),
    notes: zod_1.z.string().nullable(),
    products: models_2.ZPrizeOrderProductSelect.merge(models_1.ZProductSelect.pick({
        name: true,
        price: true,
        productCode: true,
        discountCode: true,
        purchaseFrom: true,
    })).array(),
}).array();
const ZUpdatePrizeOrderRequest = models_2.ZPrizeOrderInsert.pick({
    id: true,
    status: true,
    assigneeId: true,
    deliveryStreet: true,
    deliveryCity: true,
    deliveryPostcode: true,
    emailAddress: true,
    phoneNumber: true,
    additionalInfoFromPlayer: true,
})
    .required({
    id: true,
})
    .partial({
    additionalInfoFromPlayer: true,
});
const ZUpdatePrizeOrderResponse = zod_1.z.object({
    prizeOrder: models_2.ZPrizeOrderSelect,
    prizeOrderProducts: models_2.ZPrizeOrderProductSelect.array().optional(),
});
const ZUpdatePrizeOrderProductRequest = models_2.ZPrizeOrderProductInsert.pick({
    id: true,
    status: true,
    orderNumber: true,
    trackingNumber: true,
}).required({
    id: true,
});
const ZUpdatePrizeOrderProductResponse = zod_1.z.object({
    prizeOrderProduct: models_2.ZPrizeOrderProductSelect,
    prizeOrder: models_2.ZPrizeOrderSelect.optional(),
});
const ZGetPrizeOrderProductsResponse = models_2.ZPrizeOrderSelect.pick({
    prizeWinId: true,
    deliveryStreet: true,
    deliveryCity: true,
    deliveryPostcode: true,
    emailAddress: true,
    phoneNumber: true,
    additionalInfoFromPlayer: true,
    assigneeId: true,
})
    .merge(models_1.ZProductSelect.pick({
    price: true,
    productCode: true,
    discountCode: true,
    purchaseFrom: true,
    orderForm: true,
}))
    .extend({
    createdAt: zod_1.z.coerce.date(),
    prizeOrderId: zod_1.z.string(),
    prizeOrderStatus: models_2.ZPrizeOrderSelect.shape.status,
    prizeName: zod_1.z.string(),
    prizeType: models_1.ZPrizeSelect.shape.type,
    prizeInternalInfo: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
    brand: zod_1.z.string().nullable(),
    drawId: zod_1.z.number().nullable(),
    gameType: models_2.ZDrawTypeSelect.shape.gameType.nullable(),
    drawTypeGroup: zod_1.z.string().nullable(),
    prizeStructureCode: zod_1.z.string().nullable(),
    prizeTierCode: zod_1.z.string().nullable(),
    tokenRedemptionId: zod_1.z.string().nullable(),
    tokenRedemptionOptionTitle: zod_1.z.string().nullable(),
    playerName: zod_1.z.string(),
    playerFirstName: zod_1.z.string(),
    playerLastName: zod_1.z.string(),
    playerEmail: zod_1.z.string(),
    assigneeName: zod_1.z.string().nullable(),
    notes: zod_1.z.string().nullable(),
    prizeOrderProductId: zod_1.z.string(),
    prizeOrderProductStatus: models_2.ZPrizeOrderProductSelect.shape.status,
    orderNumber: zod_1.z.string().nullable(),
    trackingNumber: zod_1.z.string().nullable(),
    productName: zod_1.z.string(),
    internalProductName: zod_1.z.string().nullable(),
})
    .array();
const ZGetDrawsWithPrizeWinsResponse = models_2.ZDrawSelect.pick({
    id: true,
    name: true,
})
    .extend((0, types_1.ensureSubsetOfKeys)(models_2.ZDrawSelect.shape, {
    drawDate: zod_1.z.coerce.date().nullable(),
}))
    .extend({
    gameType: models_2.ZDrawTypeSelect.shape.gameType,
    drawTypeGroup: models_2.ZDrawTypeSelect.shape.group,
})
    .array();
const ZUnsetPrizeChoiceRequest = zod_1.z.object({
    prizeWinId: zod_1.z.string(),
});
const ZUnsetPrizeChoiceResponse = zod_1.z.object({
    prizeWin: models_2.ZPrizeWinSelect.optional(),
    cancelledPrizeOrder: models_2.ZPrizeOrderSelect.optional(),
});
const ZGetWinnerManagerResponse = models_2.ZPlayerSelect.pick({
    id: true,
    firstName: true,
}).array();
const c = (0, core_1.initContract)();
exports.adminWinnerContract = c.router({
    getPrizeWinsForPlayer: {
        method: 'GET',
        path: '/player/:playerId',
        pathParams: zod_1.z.object({
            playerId: zod_1.z.string(),
        }),
        responses: {
            200: ZGetPrizeWinsResponse,
        },
        summary: 'Get prize wins of a draw',
    },
    getPrizeWinsOfDraw: {
        method: 'GET',
        path: '/get-prize-wins-of-draw',
        query: zod_1.z.object({
            drawId: zod_1.z.coerce.number(),
        }),
        responses: {
            200: ZGetPrizeWinsResponse,
        },
        summary: 'Get prize wins of a draw',
    },
    getPrizeWinsOfAllRaffleDraws: {
        method: 'GET',
        path: '/get-prize-wins-of-all-raffle-draws',
        responses: {
            200: ZGetPrizeWinsResponse,
        },
        summary: 'Get prize wins of all raffle draws',
    },
    getPrizeWinsOfAllMinilottoDraws: {
        method: 'GET',
        path: '/get-prize-wins-of-all-minilotto-draws',
        responses: {
            200: ZGetPrizeWinsResponse,
        },
        summary: 'Get prize wins of all minilotto draws',
    },
    getPrizeWinsAwardedHighValued: {
        method: 'GET',
        path: '/get-prize-wins-awarded-high-valued',
        responses: {
            200: ZGetPrizeWinsResponse,
        },
        summary: 'Get awarded high-valued prize wins of all lottery draws',
    },
    updatePrizeWin: {
        method: 'PATCH',
        path: '/update-prize-win',
        body: ZUpdatePrizeWinRequest,
        responses: {
            200: ZUpdatePrizeWinResponse,
            400: c.type(),
        },
    },
    getDrawsWithPrizeWins: {
        method: 'GET',
        path: '/get-draws-with-prize-wins',
        responses: {
            200: ZGetDrawsWithPrizeWinsResponse,
        },
        summary: 'Get IDs and names of draws with prize wins',
    },
    getPrizeOrdersUnfulfilledRecent: {
        method: 'GET',
        path: '/get-prize-orders-unfulfilled-recent',
        responses: {
            200: ZGetPrizeOrdersResponse,
        },
        summary: 'Get unfufilled and recently fulfilled prize orders',
    },
    getTokenRedeemedPrizesOfPlayer: {
        method: 'GET',
        path: '/player/:playerId/token-redeemed-prizes',
        responses: {
            200: ZGetPrizeOrdersResponse,
        },
        summary: 'Get token redeemed prizes of a player',
    },
    updatePrizeOrder: {
        method: 'PATCH',
        path: '/update-prize-order',
        body: ZUpdatePrizeOrderRequest,
        responses: {
            200: ZUpdatePrizeOrderResponse,
            400: c.type(),
        },
    },
    markPrizeOrdersAsConfirmed: {
        method: 'POST',
        path: '/mark-prize-orders-as-confirmed',
        body: zod_1.z.string().array().min(1),
        responses: {
            200: utils_1.successSchema,
        },
        summary: 'Mark prize orders as confirmed in batch',
    },
    markPrizeOrderProductsAsConfirmed: {
        method: 'POST',
        path: '/mark-prize-order-products-as-confirmed',
        body: zod_1.z.string().array().min(1),
        responses: {
            200: utils_1.successSchema,
        },
        summary: 'Mark prize order products as confirmed in batch',
    },
    getPrizeOrderProducts: {
        method: 'GET',
        path: '/prize-order-products',
        query: zod_1.z.object({
            orderForm: utils_1.booleanParamSchema.optional(),
            treatWins: utils_1.booleanParamSchema.optional(),
        }),
        responses: {
            200: ZGetPrizeOrderProductsResponse,
        },
        summary: 'Get unfulfilled and recently fulfilled prize order products',
    },
    updatePrizeOrderProduct: {
        method: 'PATCH',
        path: '/update-prize-order-product',
        body: ZUpdatePrizeOrderProductRequest,
        responses: {
            200: ZUpdatePrizeOrderProductResponse,
            400: c.type(),
        },
    },
    unsetPrizeChoice: {
        method: 'POST',
        path: '/unset-prize-choice',
        body: ZUnsetPrizeChoiceRequest,
        responses: {
            200: ZUnsetPrizeChoiceResponse,
        },
        summary: "Unset player's prize choice for reselection",
    },
    resendPrizeWinEmail: {
        method: 'POST',
        path: '/resend-prize-win-email',
        body: zod_1.z.object({
            prizeWinId: zod_1.z.string(),
        }),
        responses: {
            200: utils_1.successSchema,
            400: c.type(),
        },
        summary: 'Resend the prize win notification email for a specific prize win',
    },
    submitPrizeChoice: {
        method: 'POST',
        path: '/submit-prize-choice',
        body: models_2.ZSubmitPrizeChoiceRequest,
        responses: {
            200: zod_1.z.object({
                prizeOrderId: zod_1.z.string(),
                prizeOrderProductIds: zod_1.z.string().array(),
            }),
            400: c.type(),
        },
        summary: 'Submit a prize choice on behalf of a player',
    },
    amendPrizeChoice: {
        method: 'POST',
        path: '/amend-prize-choice',
        body: models_2.ZSubmitPrizeChoiceRequest,
        responses: {
            200: zod_1.z.object({
                prizeOrderId: zod_1.z.string(),
                prizeOrderProductIds: zod_1.z.string().array(),
            }),
            400: c.type(),
        },
        summary: 'Cancel the current prize choice and submit a new one on behalf of a player',
    },
    getWinnerManagers: {
        method: 'GET',
        path: '/get-winner-managers',
        responses: {
            200: ZGetWinnerManagerResponse,
        },
    },
}, {
    pathPrefix: '/admin',
    baseHeaders: zod_1.z.object({
        'x-access-token': zod_1.z.string(),
    }),
    commonResponses: {
        401: c.type(),
        403: c.type(),
    },
});
//# sourceMappingURL=winner.admin.contract.js.map